import type {
  NavigationTimingLike,
  NetworkInformationLike,
  NetworkDownlinkSource,
} from './networkSnapshot';
import { isBotUA } from '@/utils/bot-detection';

const timeFields = {
  startTime: 'nav_start_ms',
  fetchStart: 'nav_fetch_start_ms',
  domainLookupStart: 'nav_dns_start_ms',
  domainLookupEnd: 'nav_dns_end_ms',
  connectStart: 'nav_connect_start_ms',
  connectEnd: 'nav_connect_end_ms',
  secureConnectionStart: 'nav_tls_start_ms',
  requestStart: 'nav_request_start_ms',
  responseStart: 'nav_response_start_ms',
  responseEnd: 'nav_response_end_ms',
  redirectStart: 'nav_redirect_start_ms',
  redirectEnd: 'nav_redirect_end_ms',
  workerStart: 'nav_worker_start_ms',
  activationStart: 'nav_activation_start_ms',
  finalResponseHeadersStart: 'nav_final_headers_start_ms',
} as const;

const orderedFields = [
  'startTime',
  'fetchStart',
  'domainLookupStart',
  'domainLookupEnd',
  'connectStart',
  'connectEnd',
  'requestStart',
  'responseStart',
] as const;

export interface NavigationAssociation {
  nav_id: string;
  nav_lifecycle_index: number;
}

export interface NavigationDiagnosticPayload extends NavigationAssociation {
  [key: string]: string | number | boolean;
  nav_schema_version: 1;
  nav_snapshot_seq: 1 | 2;
  nav_validity: 'valid' | 'partial' | 'nonstandard' | 'invalid';
  nav_invalid_reasons: string;
}

export interface NavigationContext {
  userAgent?: string;
  entry?: NavigationTimingLike;
  now: number;
  initPath: string;
  referrer?: string;
  visibility?: string;
  prerendering?: boolean;
  wasDiscarded?: boolean;
  swControlled?: boolean;
  swScript?: string;
  connection?: NetworkInformationLike;
}

export interface NavigationDiagnosticOptions {
  sampleRate?: number;
  random?: () => number;
  release?: string;
  legacyDownlinkSource?: NetworkDownlinkSource;
  read?: () => NavigationContext;
  id?: () => string;
  onRestore?: (callback: () => void) => () => void;
}

export interface NavigationDiagnosticHandle {
  association: (navigationType?: string) => NavigationAssociation;
  complete: (
    entry?: NavigationTimingLike,
    navigationType?: string,
  ) => NavigationDiagnosticPayload | undefined;
  dispose: () => void;
}

function finite(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

function parseUrl(value?: string): URL | undefined {
  try {
    const url = new URL(value ?? '');
    return ['https:', 'http:'].includes(url.protocol) ? url : undefined;
  } catch {
    return undefined;
  }
}

/** Arithmetic intervals only; these names deliberately do not assert network causes. */
export function calculateNavigationPhases(entry: NavigationTimingLike) {
  const values = orderedFields.map((key) => entry[key]);
  if (!values.every(finite)) return undefined;
  const [s, f, ds, de, cs, ce, rq, rs] = values;
  if (values.some((value, i) => value < 0 || (i > 0 && value < values[i - 1]))) {
    return undefined;
  }
  return {
    nav_phase_a_ms: f - s,
    nav_phase_b_ms: ds - f,
    nav_phase_c_ms: cs - de,
    nav_phase_d_ms: rq - ce,
    nav_pre_raw_ms: rq - s,
    nav_residual_raw_ms: rq - s - (de - ds) - (ce - cs),
    nav_request_wait_raw_ms: rs - rq,
  };
}

export function buildNavigationDiagnosticPayload(
  context: NavigationContext,
  options: {
    id: string;
    sampleRate: number;
    release?: string;
    legacyDownlinkSource?: NetworkDownlinkSource;
    seq: 1 | 2;
    navigationType?: string;
  },
): NavigationDiagnosticPayload {
  const entry = context.entry;
  const url = parseUrl(entry?.name);
  const type = ['navigate', 'reload', 'back_forward'].includes(entry?.type ?? '')
    ? entry!.type!
    : 'unknown';
  const reasons: string[] = [];
  const missing = !entry || orderedFields.some((key) => entry[key] === undefined);
  const nonfinite =
    !!entry && orderedFields.some((key) => entry[key] !== undefined && !finite(entry[key]));
  const nonstandard =
    context.prerendering === true ||
    (entry?.activationStart ?? 0) > 0 ||
    options.navigationType === 'prerender' ||
    options.navigationType === 'back-forward-cache';
  if (!entry) reasons.push('missing_entry');
  if (missing) reasons.push('missing_timestamp');
  if (nonfinite) reasons.push('not_finite');
  if (entry && !missing && !nonfinite && !calculateNavigationPhases(entry))
    reasons.push('bad_order');
  if (nonstandard)
    reasons.push(options.navigationType === 'back-forward-cache' ? 'bfcache' : 'prerender');
  if (type === 'unknown') reasons.push('unknown_lifecycle');
  const payload: NavigationDiagnosticPayload = {
    nav_schema_version: 1,
    nav_id: options.id,
    nav_lifecycle_index: 0,
    nav_snapshot_seq: options.seq,
    nav_capture_reason: options.seq === 1 ? 'initial' : 'complete',
    nav_capture_ms: context.now,
    nav_sample_rate: options.sampleRate,
    nav_release: (options.release || 'unknown').slice(0, 128),
    nav_entry_origin: (url?.origin ?? '').slice(0, 256),
    nav_entry_path: (url?.pathname ?? '').slice(0, 256),
    nav_init_path: context.initPath.slice(0, 256),
    nav_type: type,
    nav_bfcache_restored: options.navigationType === 'back-forward-cache',
    nav_visibility_at_capture: context.visibility ?? 'unknown',
    nav_validity: nonstandard
      ? 'nonstandard'
      : nonfinite || reasons.includes('bad_order')
        ? 'invalid'
        : missing || type === 'unknown'
          ? 'partial'
          : 'valid',
    nav_invalid_reasons: reasons.join(';'),
  };
  const supported: string[] = [];
  if (typeof context.userAgent === 'string') payload.xn_is_spider = isBotUA(context.userAgent);
  for (const [source, target] of Object.entries(timeFields)) {
    const key = source as keyof typeof timeFields;
    if (entry && key in entry) supported.push(key);
    if (finite(entry?.[key])) payload[target] = entry[key]!;
  }
  payload.nav_supported_fields = supported.join(',');
  for (const key of ['source', 'medium', 'campaign']) {
    const value = url?.searchParams.get(`utm_${key}`);
    if (value) payload[`nav_entry_utm_${key}`] = value.slice(0, 128);
  }
  const referrer = parseUrl(context.referrer);
  if (referrer) payload.nav_referrer_origin = referrer.origin.slice(0, 256);
  if (
    ['navigate', 'reload', 'back-forward', 'back-forward-cache', 'prerender', 'restore'].includes(
      options.navigationType ?? '',
    )
  ) {
    payload.nav_metric_navigation_type = options.navigationType!;
  }
  if (typeof context.prerendering === 'boolean')
    payload.nav_prerendering_at_capture = context.prerendering;
  if (typeof context.wasDiscarded === 'boolean') payload.nav_was_discarded = context.wasDiscarded;
  if (typeof context.swControlled === 'boolean')
    payload.nav_sw_controlled_at_capture = context.swControlled;
  const sw = parseUrl(context.swScript);
  if (sw) {
    payload.nav_sw_script_origin = sw.origin.slice(0, 256);
    payload.nav_sw_script_path = sw.pathname.slice(0, 256);
  }
  for (const [key, field] of [
    ['redirectCount', 'nav_redirect_count'],
    ['transferSize', 'nav_transfer_bytes'],
    ['encodedBodySize', 'nav_encoded_bytes'],
    ['decodedBodySize', 'nav_decoded_bytes'],
  ] as const) {
    if (finite(entry?.[key])) payload[field] = entry[key]!;
  }
  if (entry?.nextHopProtocol) payload.nav_protocol = entry.nextHopProtocol.slice(0, 32);
  if (typeof entry?.deliveryType === 'string')
    payload.nav_delivery_type = entry.deliveryType.slice(0, 32);
  payload.nav_net_downlink_source = options.legacyDownlinkSource ?? 'unknown';
  // Keep raw timing intact; trim optional text if URL data would exceed the event budget.
  const textFields = [
    'nav_entry_utm_campaign',
    'nav_entry_utm_medium',
    'nav_entry_utm_source',
    'nav_sw_script_path',
    'nav_sw_script_origin',
    'nav_referrer_origin',
    'nav_init_path',
    'nav_release',
  ];
  for (const key of textFields) {
    if (typeof payload[key] === 'string') {
      const clean = Array.from(payload[key])
        .filter((character) => {
          const code = character.charCodeAt(0);
          return code >= 32 && code !== 127;
        })
        .join('');
      if (clean !== payload[key]) payload.nav_text_truncated = true;
      payload[key] = clean;
    }
  }
  const encoder = new TextEncoder();
  for (const key of textFields) {
    if (encoder.encode(JSON.stringify(payload)).byteLength <= 4096) break;
    delete payload[key];
    payload.nav_text_truncated = true;
  }
  return payload;
}

export function readNavigationContext(): NavigationContext {
  const doc = document as Document & { prerendering?: boolean; wasDiscarded?: boolean };
  const nav = navigator as Navigator & { connection?: NetworkInformationLike };
  // Reading controller can be restricted by the browser; it must not discard core timing.
  let swControlled: boolean | undefined;
  let swScript: string | undefined;
  try {
    if ('serviceWorker' in nav) {
      swControlled = !!nav.serviceWorker.controller;
      swScript = nav.serviceWorker.controller?.scriptURL;
    }
  } catch {
    /* Optional context unavailable. */
  }
  return {
    entry: performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined,
    userAgent: nav.userAgent,
    now: performance.now(),
    initPath: window.location.pathname,
    referrer: doc.referrer,
    visibility: doc.visibilityState,
    prerendering: doc.prerendering,
    wasDiscarded: doc.wasDiscarded,
    swControlled,
    swScript,
    connection: nav.connection,
  };
}

function listenForRestore(callback: () => void): () => void {
  const listener = (event: PageTransitionEvent) => {
    if (event.persisted) callback();
  };
  // web-vitals uses capture listeners and can report TTFB synchronously on restore.
  // Register in the same phase before it so every metric sees the new lifecycle.
  window.addEventListener('pageshow', listener, true);
  return () => window.removeEventListener('pageshow', listener, true);
}

/** Owner initializes once per document. Every diagnostic failure is isolated from legacy metrics. */
export function startNavigationDiagnostics(
  options: NavigationDiagnosticOptions,
): NavigationDiagnosticHandle | undefined {
  let dispose = () => {};
  try {
    const value = options.sampleRate;
    const sampleRate = finite(value) && value >= 0 && value <= 1 ? value : 0;
    if (sampleRate === 0) return undefined;
    if (sampleRate < 1 && (options.random ?? Math.random)() >= sampleRate) return undefined;
    const read = options.read ?? readNavigationContext;
    const initial = read();
    const id = options.id?.() ?? crypto.randomUUID();
    let lifecycle = 0;
    let completed = false;
    dispose = (options.onRestore ?? listenForRestore)(() => {
      lifecycle += 1;
    });
    return {
      association: (type) => ({
        nav_id: id,
        nav_lifecycle_index: type === 'back-forward-cache' ? Math.max(1, lifecycle) : 0,
      }),
      complete: (entry, type) => {
        if (type === 'back-forward-cache') {
          completed = true;
          return;
        }
        if (completed || lifecycle > 0) return;
        completed = true;
        try {
          const latest = read();
          return buildNavigationDiagnosticPayload(
            { ...latest, initPath: initial.initPath, entry: entry ?? latest.entry },
            {
              id,
              sampleRate,
              release: options.release,
              legacyDownlinkSource: options.legacyDownlinkSource,
              seq: 2,
              navigationType: type,
            },
          );
        } catch {
          /* Omit diagnostics on failure; the existing TTFB event must still be sent. */
        }
      },
      dispose,
    };
  } catch {
    dispose();
    return undefined;
  }
}
